<template>
    <div class="scanner" :class="{'scanner--inline': inline}">
        <div class="scanner__video-container" v-show="isScanning">
            <video
                ref="video"
                class="scanner__video"
                autoplay
                playsinline
                muted
            ></video>
            <div class="scanner__overlay">
                <div class="scanner__frame"></div>
            </div>
            <button
                class="scanner__close-btn"
                @click="stopScanning"
            >
                &times;
            </button>
            <div class="scanner__status-bar" v-if="isScanning">
                <span class="scanner__status-dot"></span>
                Skanowanie...
            </div>
        </div>

        <div class="scanner__controls" v-if="!inline">
            <button
                class="button is-primary is-medium"
                @click="startScanning"
                v-if="!isScanning"
                :disabled="!!error"
            >
                Rozpocznij skanowanie
            </button>
            <button
                class="button is-danger is-medium"
                @click="stopScanning"
                v-if="isScanning"
            >
                Zatrzymaj skanowanie
            </button>
        </div>

        <div class="scanner__result" v-if="lastResult && !inline">
            <div class="notification is-success">
                <p class="scanner__result-label">Wykryty kod:</p>
                <p class="scanner__result-value">{{ lastResult.rawValue }}</p>
                <p class="scanner__result-format">Format: {{ lastResult.format }}</p>
            </div>
        </div>

        <div class="scanner__error" v-if="error">
            <div class="notification is-danger">
                {{ error }}
            </div>
        </div>
    </div>
</template>

<script>
import { BarcodeDetector } from 'barcode-detector/dist/es/ponyfill.js';

export default {
    name: 'TheBarcodeScanner',
    props: {
        inline: {
            type: Boolean,
            default: false
        },
        autoStop: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isScanning: false,
            lastResult: null,
            error: null,
            stream: null,
            detector: null,
            animationFrameId: null,
            pendingCode: null,
            pendingCount: 0,
            requiredConfirmations: 3,
            scanInterval: 200,
            lastScanTime: 0
        }
    },
    methods: {
        async initDetector() {
            try {
                this.detector = new BarcodeDetector({
                    formats: [
                        'qr_code',
                        'ean_13',
                        'ean_8',
                        'code_128',
                        'code_39',
                        'code_93',
                        'upc_a',
                        'upc_e',
                        'itf',
                        'data_matrix',
                        'aztec',
                        'pdf417'
                    ]
                });
            } catch (err) {
                this.error = 'Nie udało się zainicjalizować detektora kodów: ' + err.message;
                this.$emit('error', this.error);
            }
        },
        async startScanning() {
            this.error = null;
            this.lastResult = null;

            try {
                this.stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'environment',
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    }
                });

                this.$refs.video.srcObject = this.stream;
                await this.$refs.video.play();
                this.isScanning = true;
                this.$emit('scanning', true);
                this.detectLoop();
            } catch (err) {
                if (err.name === 'NotAllowedError') {
                    this.error = 'Dostęp do kamery został zablokowany. Zezwól na dostęp w ustawieniach przeglądarki.';
                } else if (err.name === 'NotFoundError') {
                    this.error = 'Nie znaleziono kamery na tym urządzeniu.';
                } else {
                    this.error = 'Błąd dostępu do kamery: ' + err.message;
                }
                this.$emit('error', this.error);
            }
        },
        async detectLoop() {
            if (!this.isScanning || !this.detector) return;

            var now = Date.now();
            if (now - this.lastScanTime < this.scanInterval) {
                this.animationFrameId = requestAnimationFrame(() => this.detectLoop());
                return;
            }
            this.lastScanTime = now;

            var video = this.$refs.video;
            if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
                try {
                    var barcodes = await this.detector.detect(video);
                    if (barcodes.length > 0) {
                        var code = barcodes[0];
                        if (this.pendingCode === code.rawValue) {
                            this.pendingCount++;
                        } else {
                            this.pendingCode = code.rawValue;
                            this.pendingCount = 1;
                        }

                        if (this.pendingCount >= this.requiredConfirmations) {
                            this.lastResult = code;
                            this.$emit('detected', code);
                            this.pendingCode = null;
                            this.pendingCount = 0;
                            if (this.autoStop) {
                                this.stopScanning();
                                return;
                            }
                        }
                    } else {
                        this.pendingCode = null;
                        this.pendingCount = 0;
                    }
                } catch (err) {
                    console.error('Detection error:', err);
                }
            }

            this.animationFrameId = requestAnimationFrame(() => this.detectLoop());
        },
        stopScanning() {
            this.isScanning = false;
            this.$emit('scanning', false);

            if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
            }

            if (this.stream) {
                this.stream.getTracks().forEach(function (track) { track.stop() });
                this.stream = null;
            }

            if (this.$refs.video) {
                this.$refs.video.srcObject = null;
            }
        }
    },
    async mounted() {
        await this.initDetector();
    },
    beforeDestroy() {
        this.stopScanning();
    }
}
</script>

<style lang="sass" scoped>
@import "@/styles/variables"

.scanner
    display: flex
    flex-direction: column
    align-items: center
    gap: 1rem
    padding: 1rem

    &--inline
        padding: 0
        gap: 0.5rem

    &__video-container
        position: relative
        width: 100%
        max-width: 500px
        border-radius: 8px
        overflow: hidden
        background: #000
        height: 300px

    &__video
        width: 100%
        display: block

    &__overlay
        position: absolute
        top: 0
        left: 0
        right: 0
        bottom: 0
        display: flex
        align-items: center
        justify-content: center

    &__frame
        width: 60%
        height: 60%
        border: 2px solid rgba(255, 255, 255, 0.7)
        border-radius: 12px
        box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3)

    &__close-btn
        position: absolute
        top: 0.5rem
        right: 0.5rem
        background: rgba(0, 0, 0, 0.6)
        color: #fff
        border: none
        border-radius: 50%
        width: 2rem
        height: 2rem
        font-size: 1.2rem
        line-height: 1
        cursor: pointer
        z-index: 10

    &__status-bar
        position: absolute
        bottom: 0
        left: 0
        right: 0
        background: rgba(0, 0, 0, 0.6)
        color: #fff
        text-align: center
        padding: 0.25rem
        font-size: 0.75rem

    &__status-dot
        display: inline-block
        width: 8px
        height: 8px
        background: #ff3860
        border-radius: 50%
        margin-right: 0.25rem
        animation: blink 1s infinite

    &__controls
        display: flex
        gap: 0.5rem

    &__result
        width: 100%
        max-width: 500px

    &__result-label
        font-weight: bold
        margin-bottom: 0.25rem

    &__result-value
        font-size: 1.25rem
        word-break: break-all

    &__result-format
        font-size: 0.85rem
        opacity: 0.8
        margin-top: 0.25rem

    &__error
        width: 100%
        max-width: 500px

@keyframes blink
    0%, 100%
        opacity: 1
    50%
        opacity: 0.3
</style>
